import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import urljoin from 'url-join';

import Layout from '@/components/Layout/Vanilla';
import Card from '@/components/blog/card';
import CardsContainer from '@/components/blog/cards-container';
import Heading from '@/components/Heading';
import Pagination from '@/components/blog/pagination';
import Breadcrump, { BreadcrumpPages } from '@/components/blog/breadcrump';
import { fetchAPI, gql } from '@/lib/graphcms';
import { Post, Category } from '@/types';
import { getPagination } from '@/utils/blog';
import { defaultSeoUrl } from '../../../../next-seo.config';

type TagProps = {
  category: Category;
  blogPostsConnection: {
    blogPosts: {
      node: Post;
    }[];
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
    aggregate: {
      count: number;
    };
  };
  currentPage: number;
  isFirstPage: boolean;
  preview: boolean;
};

const limit = 9;

export default function Tag({
  category,
  blogPostsConnection: { blogPosts, aggregate, pageInfo },
  currentPage,
  isFirstPage,
  preview,
}: TagProps) {
  const router = useRouter();

  const pageUrl = urljoin(defaultSeoUrl, router.asPath);
  const pageTitle = !isFirstPage
    ? `Page ${currentPage} | ${category.title}`
    : category.title;

  const breadcrumpPages: BreadcrumpPages = [
    {
      name: `Blog`,
      href: `/blog`,
    },
    {
      name: category.title,
      href: `/blog/tag/${category.slug}`,
      current: true,
    },
  ];

  return (
    <>
      <Head>
        {pageInfo.hasPreviousPage && (
          <link
            rel="prev"
            href={`/blog/tag/${category.slug}?page=${currentPage - 1}`}
          />
        )}
        {pageInfo.hasNextPage && (
          <link
            rel="next"
            href={`/blog/tag/${category.slug}?page=${currentPage + 1}`}
          />
        )}
        {!isFirstPage && <link rel="canonical" href={pageUrl} />}
      </Head>

      <Layout
        preview={preview}
        seo={{
          title: pageTitle,
          meta: category.meta,
        }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-10">
            <Breadcrump pages={breadcrumpPages} />
          </div>

          <Heading>{category.title}</Heading>
          {category?.description && (
            <h2 className="text-light-haiti text-lg leading-7 mt-1">
              {category.description}
            </h2>
          )}

          <hr
            aria-orientation="horizontal"
            className="h-0 border-t border-primary-100 mt-6 hidden md:block"
          />

          {blogPosts.length > 0 ? (
            <>
              <CardsContainer className="mt-10 md:mt-20">
                {blogPosts.map(({ node }) => (
                  <Card key={node.id} post={node} />
                ))}
              </CardsContainer>

              <div className="my-20">
                <Pagination
                  currentPage={currentPage}
                  totalCount={aggregate.count}
                  pageSize={limit}
                  onPageChange={(page) => {
                    router.push(`/blog/tag/${category.slug}?page=${page}`);
                  }}
                />
              </div>
            </>
          ) : (
            <div className="py-20 text-light-haiti">
              We couldn&apos;t find any posts for this tag.
            </div>
          )}
        </div>
      </Layout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  preview = false,
  params,
  res,
  query,
}) => {
  const { page, offset } = getPagination({
    queryPage: query.page,
    limit,
  });

  const { category, blogPostsConnection } =
    (await fetchAPI<TagProps>(
      gql`
        fragment seoData on Seo {
          description
          image {
            url
            width
            height
          }
          keywords
          noindex
          title
        }

        fragment assetDataXL on Asset {
          url(transformation: { image: { resize: { width: 960, fit: clip } } })
          width
          height
          alt
        }

        fragment categoryData on Category {
          id
          slug
          title
        }

        query ($slug: String!, $limit: Int!, $offset: Int!) {
          category(where: { slug: $slug }) {
            ...categoryData
          }
          blogPostsConnection(
            orderBy: publishedOn_DESC
            where: { categories_some: { slug: $slug } }
            first: $limit
            skip: $offset
          ) {
            blogPosts: edges {
              node {
                id
                slug
                title
                subTitle
                coverImage {
                  ...assetDataXL
                }
                authors {
                  name
                  photo {
                    url(
                      transformation: {
                        image: { resize: { width: 300, fit: clip } }
                      }
                    )
                    width
                    height
                    alt
                  }
                }
                categories {
                  ...categoryData
                }
                meta: seo {
                  ...seoData
                }
                publishedOn
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
            }
            aggregate {
              count
            }
          }
        }
      `,
      {
        preview,
        variables: {
          slug: params?.slug,
          limit,
          offset,
        },
      },
    )) || [];

  if (!category) {
    return {
      notFound: true,
    };
  }

  // If the page param is bigger than the total number of pages, redirect to the tag page
  const totalNumberOfPages = Math.ceil(
    blogPostsConnection.aggregate.count / limit,
  );

  if (page > totalNumberOfPages) {
    return {
      redirect: {
        destination: `/blog/tag/${query.slug}`,
        permanent: false,
      },
    };
  }

  const isFirstPage = page === 1;

  res.setHeader(`Cache-Control`, `s-maxage=3600, stale-while-revalidate`);

  return {
    props: {
      preview,
      category,
      blogPostsConnection,
      currentPage: page,
      isFirstPage,
    },
  };
};
